import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Plus, Users, Briefcase, ArrowsClockwise, Warning, CheckCircle, Trash } from "@phosphor-icons/react";

const HR_ROLES = new Set(["super_admin", "company_admin", "hr", "manager"]);

export default function Allocations() {
  const { user } = useAuth();
  const isAdmin = HR_ROLES.has(user?.role);
  const [heatmap, setHeatmap] = useState(null);
  const [allocs, setAllocs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [{ data: hm }, { data: al }, { data: pj }, { data: em }] = await Promise.all([
        api.get("/pmo/allocations/heatmap", { params: { weeks: 8 } }),
        api.get("/pmo/allocations"),
        api.get("/projects").catch(() => ({ data: [] })),
        api.get("/employees").catch(() => ({ data: [] })),
      ]);
      setHeatmap(hm); setAllocs(al); setProjects(pj); setEmployees(em);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const stats = useMemo(() => {
    if (!heatmap) return { active: 0, allocated: 0, avg: 0, overloaded: 0, bench: 0 };
    const rows = heatmap.rows || [];
    const perEmpTotal = rows.map((r) => r.cells.reduce((s, c) => s + c, 0));
    const allocated = perEmpTotal.filter((t) => t > 0).length;
    const bench = perEmpTotal.filter((t) => t === 0).length;
    const overloaded = rows.filter((r) => r.cells.some((c) => c > 100)).length;
    const filled = rows.filter((r) => r.cells.some((c) => c > 0));
    const avg = filled.length ? Math.round(filled.reduce((s, r) => s + (r.cells.reduce((a, c) => a + c, 0) / r.cells.length), 0) / filled.length) : 0;
    return { active: projects.length, allocated, avg, overloaded, bench };
  }, [heatmap, projects]);

  const onDelete = async (aid) => {
    if (!window.confirm("Remove this allocation?")) return;
    try { await api.delete(`/pmo/allocations/${aid}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="allocations-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-zenith-teal font-bold">Project allocations</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Right people. Right projects.</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">Allocate % of each employee's time to projects and see capacity-vs-load across the next 8 weeks. Anyone above 100% is overloaded.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} className="rounded-sm" data-testid="alloc-refresh">
            <ArrowsClockwise size={14} className={busy ? "animate-spin mr-1" : "mr-1"} /> Refresh
          </Button>
          {isAdmin && <NewAllocationDialog projects={projects} employees={employees} onCreated={load} />}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Active projects" value={stats.active} tone="teal" />
        <Stat label="Allocated" value={stats.allocated} tone="green" />
        <Stat label="Avg utilization" value={`${stats.avg}%`} tone="navy" />
        <Stat label="Overloaded" value={stats.overloaded} tone="magenta" icon={Warning} />
        <Stat label="On bench" value={stats.bench} tone="zinc" />
      </div>

      {/* Heatmap */}
      <div className="border border-zinc-200 rounded-md bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <div className="font-display font-bold text-sm text-adb-navy">Capacity vs Load — next 8 weeks</div>
            <div className="text-[11px] text-zinc-500 mt-0.5">
              <LegendDot tone="green" /> 0–70% healthy
              <LegendDot tone="amber" /> 71–100% full
              <LegendDot tone="red" /> &gt; 100% overloaded
              <LegendDot tone="zinc" /> 0% bench
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="alloc-heatmap-table">
            <thead className="bg-zinc-50 sticky top-0 z-10">
              <tr className="text-left">
                <th className="py-2 px-3 text-[10px] uppercase tracking-widest text-zinc-500 w-64">Employee</th>
                {(heatmap?.weeks || []).map((w) => (
                  <th key={w.start} className="py-2 px-1 text-[10px] uppercase tracking-widest text-zinc-500 text-center">{w.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(heatmap?.rows || []).map((r) => (
                <tr key={r.employee_id} className="border-t border-zinc-100" data-testid={`heatmap-row-${r.employee_id}`}>
                  <td className="py-2 px-3 min-w-[16rem]">
                    <div className="font-semibold text-adb-navy text-sm truncate">{r.name}</div>
                    <div className="text-[11px] text-zinc-500 truncate">{r.designation || "—"} · <span className="font-mono-plex">{r.employee_code}</span></div>
                  </td>
                  {r.cells.map((v, i) => <HeatCell key={i} value={v} />)}
                </tr>
              ))}
              {(!heatmap || heatmap.rows.length === 0) && (
                <tr><td colSpan={9} className="py-8 text-center text-zinc-400 text-sm">No employees yet — add employees + create your first allocation.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Allocations table */}
      <div className="border border-zinc-200 rounded-md bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-200">
          <div className="font-display font-bold text-sm text-adb-navy">All allocations</div>
        </div>
        <table className="w-full text-sm" data-testid="alloc-table">
          <thead className="bg-zinc-50 text-left">
            <tr className="text-[11px] uppercase tracking-widest text-zinc-500">
              <th className="py-2 px-3">Employee</th>
              <th className="py-2 px-3">Project</th>
              <th className="py-2 px-3">%</th>
              <th className="py-2 px-3">Period</th>
              <th className="py-2 px-3">Billable</th>
              <th className="py-2 px-3"></th>
            </tr>
          </thead>
          <tbody>
            {allocs.map((a) => (
              <tr key={a.id} className="border-t border-zinc-100">
                <td className="py-2 px-3">{a.employee_name || <span className="text-zinc-400 font-mono-plex text-xs">{a.employee_id?.slice(-6)}</span>}</td>
                <td className="py-2 px-3">{a.project_name || <span className="text-zinc-400 font-mono-plex text-xs">{a.project_id?.slice(-6)}</span>}</td>
                <td className="py-2 px-3 font-mono-plex font-bold">{a.percent}%</td>
                <td className="py-2 px-3 text-xs text-zinc-500">{a.start_date} → {a.end_date}</td>
                <td className="py-2 px-3">
                  {a.billable ? (
                    <Badge className="rounded-sm bg-zenith-green/10 text-zenith-green border-zenith-green/40 text-[10px]">Billable</Badge>
                  ) : (
                    <Badge variant="outline" className="rounded-sm text-[10px]">Internal</Badge>
                  )}
                </td>
                <td className="py-2 px-3 text-right">
                  {isAdmin && <button onClick={() => onDelete(a.id)} className="text-zinc-400 hover:text-red-600" data-testid={`alloc-delete-${a.id}`}><Trash size={14} /></button>}
                </td>
              </tr>
            ))}
            {allocs.length === 0 && (
              <tr><td colSpan={6} className="py-8 text-center text-zinc-400 text-sm">No allocations yet — click "+ Allocate" to create one.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function HeatCell({ value }) {
  const tone =
    value === 0 ? { bg: "bg-zinc-50", text: "text-zinc-300" }
    : value <= 70 ? { bg: "bg-zenith-green/20", text: "text-zenith-green" }
    : value <= 100 ? { bg: "bg-amber-100", text: "text-amber-700" }
    : { bg: "bg-red-100", text: "text-red-700" };
  return (
    <td className="p-1 text-center">
      <div className={`h-9 rounded-sm ${tone.bg} grid place-items-center text-xs font-bold ${tone.text}`} title={`${value}% allocated`}>
        {value > 0 ? `${value}%` : "—"}
      </div>
    </td>
  );
}

function LegendDot({ tone }) {
  const bg = { green: "bg-zenith-green/40", amber: "bg-amber-300", red: "bg-red-400", zinc: "bg-zinc-200" }[tone];
  return <span className={`inline-block w-2 h-2 rounded-sm mx-1 ${bg}`} />;
}

function Stat({ label, value, tone = "navy", icon: Icon }) {
  const t = {
    teal: "text-zenith-teal border-zenith-teal/30 bg-zenith-teal/5",
    green: "text-zenith-green border-zenith-green/30 bg-zenith-green/5",
    magenta: "text-zenith-magenta border-zenith-magenta/30 bg-zenith-magenta/5",
    navy: "text-adb-navy border-adb-navy/20 bg-adb-navy/5",
    zinc: "text-zinc-500 border-zinc-200 bg-zinc-50",
  }[tone];
  return (
    <div className={`border ${t} rounded-sm p-3`}>
      <div className="flex items-center gap-1.5">
        {Icon && <Icon size={12} weight="fill" />}
        <div className="text-[10px] uppercase tracking-widest font-bold opacity-70">{label}</div>
      </div>
      <div className="font-display text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}

function NewAllocationDialog({ projects, employees, onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({
    employee_id: "", project_id: "", task_name: "", percent: 50,
    start_date: new Date().toISOString().slice(0, 10),
    end_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
    billable: true, role: "", notes: "",
  });
  const submit = async () => {
    if (!f.employee_id || (!f.project_id && !f.task_name.trim())) { toast.error("Pick an employee and a project or type a task"); return; }
    try {
      await api.post("/pmo/allocations", { ...f, project_id: f.project_id || null, task_name: f.task_name.trim() || null, percent: Number(f.percent) });
      toast.success("Allocated"); setOpen(false); onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-sm bg-adb-navy hover:bg-adb-navy-800" data-testid="alloc-new-btn"><Plus size={14} className="mr-1" /> Allocate</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="alloc-new-dialog">
        <DialogHeader><DialogTitle className="font-display">New allocation</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Employee *</Label>
            <Select value={f.employee_id} onValueChange={(v) => setF({ ...f, employee_id: v })}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="alloc-emp-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
              <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name} · {e.employee_code}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Project (from list)</Label>
            <Select value={f.project_id} onValueChange={(v) => setF({ ...f, project_id: v === "__manual" ? "" : v })}>
              <SelectTrigger className="rounded-sm mt-1" data-testid="alloc-prj-select"><SelectValue placeholder="Select project — or type a task below" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__manual">— Type a task manually —</SelectItem>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {!f.project_id && (
            <div>
              <Label className="text-xs">Task / engagement name *</Label>
              <Input value={f.task_name} onChange={(e) => setF({ ...f, task_name: e.target.value })} placeholder="e.g. Internal audit support" className="rounded-sm mt-1" data-testid="alloc-task-name" />
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">% allocation</Label>
              <Input type="number" min={1} max={100} value={f.percent} onChange={(e) => setF({ ...f, percent: e.target.value })} className="rounded-sm mt-1" data-testid="alloc-percent" />
            </div>
            <div>
              <Label className="text-xs">Role on project</Label>
              <Input value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} placeholder="Tech Lead" className="rounded-sm mt-1" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Start</Label>
              <Input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} className="rounded-sm mt-1" />
            </div>
            <div>
              <Label className="text-xs">End</Label>
              <Input type="date" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} className="rounded-sm mt-1" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs text-zinc-700 cursor-pointer">
            <input type="checkbox" checked={f.billable} onChange={(e) => setF({ ...f, billable: e.target.checked })} />
            Billable to client
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={submit} className="bg-adb-navy hover:bg-adb-navy-800 rounded-sm" data-testid="alloc-submit-btn">Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
